import React, { useState } from 'react'
import { Upload, Typography, Button, Space, Card, message } from 'antd'
import {
    InboxOutlined,
    DownloadOutlined,
    UploadOutlined,
    CloseOutlined,
} from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import {
    apiDownloadSampleDoc,
    apiImportTestCases,
} from '../../../../../redux/testCases/import/importTestCase.service'
import { getCurrentProjectId } from '../../forms/helper'
import {
    setFilter,
    testCaseState,
} from '../../../../../redux/testCases/testCaseList.slice'

const { Dragger } = Upload
const { Text } = Typography

//=========================================================================================
const ImportTestCaseForm = ({
    stateTestCase = {},
    setStateTestCase = () => {},
}) => {
    const dispatch = useDispatch()

    const { defaultFilter } = useSelector(testCaseState)

    const [downloadLoading, setDownloadLoading] = useState(false)
    const [fileList, setFileList] = useState([])
    const [uploading, setUploading] = useState(false)

    const accept =
        '.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, ' +
        '.xls, application/vnd.ms-excel, '

    const projectId = getCurrentProjectId()
    const maxSizeMB = 10

    const beforeUpload = (file) => {
        const validType =
            file.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' ||
            // file.type === 'text/csv' ||
            file.name.endsWith('.xlsx') ||
            file.name.endsWith('.xls')
        // ||
        // file.name.endsWith('.csv')

        if (!validType) {
            message.error('Please upload an Excel (.xlsx/.xls) file.')
            return Upload.LIST_IGNORE
        }
        const okSize = file.size / 1024 / 1024 < maxSizeMB
        if (!okSize) {
            message.error(`File must be smaller than ${maxSizeMB}MB.`)
            return Upload.LIST_IGNORE
        }

        // keep the file locally; prevent auto upload
        setFileList([file])
        return false
    }

    const handleRemove = () => setFileList([])

    /** ---- Save (manual upload) ---- */
    const handleSave = async () => {
        if (!fileList.length) {
            message.warning('Please select a file first.')
            return
        }
        setUploading(true)
        try {
            const form = new FormData()
            form.append('upload', fileList[0])
            form.append('project', projectId)

            const resp = await apiImportTestCases(form)
            if (!resp) throw new Error('Upload failed')
            message.success('Upload successful.')
            dispatch(setFilter(defaultFilter))
            setFileList([])
            setStateTestCase({
                ...stateTestCase,
                importTCModal: false,
            })
        } catch (err) {
            message.error(err?.message || 'Upload failed.')
        } finally {
            setUploading(false)
        }
    }

    /** ---- Cancel (clear) ---- */
    const handleCancel = () => {
        setFileList([])
        setUploading(false)

        setStateTestCase({
            ...stateTestCase,
            importTCModal: false,
        })
    }

    /** ---- Download helper ---- */
    async function downloadFromApi() {
        try {
            setDownloadLoading(true)
            const res = await apiDownloadSampleDoc()
            const { data } = res
            if (data.status !== 200)
                throw new Error(`Download failed (${data.status})`)

            const link = document.createElement('a')
            link.href = URL.createObjectURL(data?.data) // expecting Blob from your API layer
            link.download = 'sample.xlsx'
            link.click()
            URL.revokeObjectURL(link.href)
        } catch (err) {
            console.error(err)
            message.error(err.message || 'Failed to download file')
        } finally {
            setDownloadLoading(false)
        }
    }

    return (
        <div style={{ marginTop: 15 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* 1) Draggable upload (no auto-upload) */}
                <Dragger
                    multiple={false}
                    maxCount={1}
                    accept={accept}
                    beforeUpload={beforeUpload}
                    onRemove={handleRemove}
                    // display the selected file
                    fileList={fileList.map((f) => ({
                        uid: f.uid || f.name,
                        name: f.name,
                        status: 'done', // just to show as selected
                        originFileObj: f,
                    }))}
                    style={{ padding: 16 }}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Drag & drop your Excel file here
                    </p>
                    <p className="ant-upload-hint">
                        or click to select. Accepted: .xlsx, .xls (max{' '}
                        {maxSizeMB}MB)
                    </p>
                </Dragger>

                {/* 2) Actions: Save/Cancel + Download */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'end',
                            width: '100%',
                            gap: 10,
                        }}
                    >
                        <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            onClick={handleSave}
                            loading={uploading}
                            disabled={!fileList.length}
                        >
                            Import
                        </Button>

                        <Button
                            onClick={handleCancel}
                            disabled={uploading && !fileList.length}
                        >
                            Cancel
                        </Button>
                    </div>
                    <div style={{ padding: 0 }}>
                        <Text>Download sample excel file</Text>
                        <Button
                            type="link"
                            // icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={() => downloadFromApi()}
                        >
                            Download
                        </Button>
                    </div>
                </div>
            </Space>
        </div>
    )
}

export default ImportTestCaseForm
